import { ACCOUNT_LENS_ADDRESS } from '@/config/euler-contracts'
import { useELendInfo } from '@/domain/euler/elends/useELendInfo'
import { useAccountLiquidity } from '@/domain/euler/positions/useAccountLiquidity'
import { useEBorrowPositions } from '@/domain/euler/positions/useEBorrowPositions'
import { useELendPositions } from '@/domain/euler/positions/useELendPositions'
import { useEnabledCollateralVaults } from '@/domain/euler/positions/useEnabledCollateralVaults'
import { getSubAccountAddress } from '@/domain/euler/shared/subAccounts'
import { useSubaccountStore } from '@/domain/euler/subaccounts'
import { useTokenInfo } from '@/domain/euler/token/useTokenInfo'
import { useSolverOptions } from '@/features/card/hooks/useSolverOptions'
import { accountLensAbi } from 'abis/evm/accountLens'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import BigNumber from 'bignumber.js'
import { useEffect, useMemo } from 'react'
import { Address, formatUnits } from 'viem'
import { useAccount, useReadContract, useReadContracts } from 'wagmi'
import { PROTOCOL_CHAIN_IDS } from '../constants'
import { useVaultClassifier } from '../contexts/VaultClassifierContext'
import { type EulerSpendPowerLiquidity, calculateEulerSpendPowerUSD } from './eulerSpendPower'
import { useCardEulerPositions } from './useCardEulerPositions'
import { useLtvWeightedCollateral } from './useLtvWeightedCollateral'

interface UseEulerCardDataParams {
  chainId: number
  borrowVaultAddress: Address | undefined
  userAddress: Address | undefined
  enabled: boolean
  subAccountId?: number
}

const SECONDS_PER_YEAR = 365.25 * 24 * 60 * 60

export function useEulerCardData({
  chainId,
  borrowVaultAddress,
  userAddress,
  enabled,
  subAccountId,
}: UseEulerCardDataParams) {
  const addSubaccount = useSubaccountStore((state) => state.addSubaccount)
  const { eulerBorrowVaults } = useSolverOptions()
  const hasValidSubAccount = Number.isInteger(subAccountId)

  useEffect(() => {
    if (userAddress) {
      if (hasValidSubAccount) {
        addSubaccount(subAccountId as number)
      }
    }
  }, [userAddress, addSubaccount, subAccountId, hasValidSubAccount])

  // Fetch Euler lend positions (collateral) for this card's sub-account
  const { positions: eulerPositions } = useCardEulerPositions({ subAccountId })

  // Fetch Euler borrow positions
  const { positions: eulerBorrowPositions } = useEBorrowPositions({
    userAddress,
    chainId,
    enabled: enabled && !!borrowVaultAddress && hasValidSubAccount,
  })

  // Fetch vault info to get asset details (this is the collateral vault, e.g., WHYPE)
  const { vault: eulerVault } = useELendInfo(enabled ? borrowVaultAddress : undefined, chainId)

  const selectedBorrowTokenAddress = useMemo(() => {
    if (!borrowVaultAddress) return undefined

    return eulerBorrowVaults.find((vault) => vault.address.toLowerCase() === borrowVaultAddress.toLowerCase())
      ?.borrowToken as Address | undefined
  }, [borrowVaultAddress, eulerBorrowVaults])

  const selectedBorrowTokenSymbol = useMemo(() => {
    if (!borrowVaultAddress) return undefined

    return eulerBorrowVaults.find((vault) => vault.address.toLowerCase() === borrowVaultAddress.toLowerCase())
      ?.borrowTokenSymbol
  }, [borrowVaultAddress, eulerBorrowVaults])

  const { token: borrowToken } = useTokenInfo(chainId, enabled ? selectedBorrowTokenAddress : undefined)

  const subAccountAddress = useMemo(() => {
    if (!userAddress || !hasValidSubAccount) return undefined
    return getSubAccountAddress(userAddress, subAccountId as number)
  }, [userAddress, subAccountId, hasValidSubAccount])

  // Prefer the sub-account's actual controller (the vault where it currently
  // has debt) as the liability vault for the lens query. The card's
  // `solverConfig.borrowVaultAddress` is the vault the card *will* borrow
  // from, but if the sub-account already has debt in a different vault, Euler
  // only allows one active controller at a time and `getAccountInfo` against
  // the wrong vault returns `queryFailure = true` — which would blank out
  // spending power on the dashboard even though real capacity exists.
  //
  // Mirrors CardConfiguration's `EulerPositionCard` logic so the dashboard and
  // the Manage view display the same spending-power number for the same
  // sub-account. Prefer a debt entry that matches the configured borrow vault
  // (keeps behaviour unchanged when they already align), then any existing
  // debt vault, then fall back to the configured borrow vault for brand-new
  // positions with no debt yet.
  const effectiveLiabilityVault = useMemo(() => {
    if (!enabled || !hasValidSubAccount) return borrowVaultAddress
    const cardSubAccountBorrows = eulerBorrowPositions.filter((p) => p.subAccountId === subAccountId)
    if (cardSubAccountBorrows.length === 0) return borrowVaultAddress
    if (borrowVaultAddress) {
      const matchingConfigured = cardSubAccountBorrows.find(
        (p) => p.liabilityVaultAddress.toLowerCase() === borrowVaultAddress.toLowerCase(),
      )
      if (matchingConfigured) return matchingConfigured.liabilityVaultAddress as Address
    }
    return cardSubAccountBorrows[0]!.liabilityVaultAddress as Address
  }, [enabled, hasValidSubAccount, eulerBorrowPositions, subAccountId, borrowVaultAddress])

  // Use useAccountLiquidity for accurate health factor and collateral calculations
  // This uses the Account Lens contract which properly considers ALL enabled collaterals
  const { data: accountLiquidityData, isLoading: isLoadingAccountLiquidity } = useAccountLiquidity({
    accountAddress: subAccountAddress,
    liabilityVault: effectiveLiabilityVault,
    chainId,
    enabled: enabled && !!subAccountAddress && !!effectiveLiabilityVault,
  })

  // Fetch Euler vault interest rate
  const { data: eulerInterestRate } = useReadContract({
    address: enabled ? borrowVaultAddress : undefined,
    abi: eVaultImplementationAbi,
    functionName: 'interestRate',
    chainId,
    query: {
      enabled: enabled && !!borrowVaultAddress,
      staleTime: 30_000, // Cache for 30 seconds
    },
  })

  // Use computed values from useAccountLiquidity
  // This provides accurate metrics considering ALL enabled collaterals via Account Lens
  const totalCollateralUSD = accountLiquidityData?.totalCollateralValueUSD ?? 0
  const totalDebtValueUSD = accountLiquidityData?.totalDebtValueUSD ?? 0
  const totalDebtBorrowUSD = accountLiquidityData?.totalDebtBorrowUSD ?? 0
  const currentLTV = accountLiquidityData?.currentLTV ?? 0
  const effectiveLiquidationLTV = accountLiquidityData?.effectiveLiquidationLTV ?? 0

  // Source of truth for "which vaults are enabled as collateral on this sub-account"
  // is the EVC's getCollaterals(subAccount) - matches the dashboard's Sub-account
  // Positions view. We intentionally do NOT use
  // `accountLiquidityData.raw.collaterals` here because that set is filtered by
  // the liability vault's LTV config and drops enabled collaterals whose LTV
  // hasn't been configured against the card's borrow vault.
  const { enabledVaults: enabledCollateralVaults, isLoading: isLoadingEnabledCollaterals } = useEnabledCollateralVaults(
    {
      accountAddress: subAccountAddress,
      chainId,
      enabled: enabled && !!subAccountAddress,
    },
  )

  // Format health factor from useAccountLiquidity
  const healthFactor = useMemo(() => {
    if (!enabled || !accountLiquidityData) return 'N/A'

    const hf = accountLiquidityData.healthFactor

    // No debt = infinite health
    if (hf === undefined) return '∞'

    // Format health factor
    return hf > 100 ? '>100' : hf.toFixed(2)
  }, [enabled, accountLiquidityData])

  const cardBorrowPositions = useMemo(() => {
    return eulerBorrowPositions.filter((p) => p.subAccountId === subAccountId)
  }, [eulerBorrowPositions, subAccountId])

  // Calculate total borrowed in USD from eBorrow positions (only from card sub-account)
  const totalBorrowUSD = useMemo(() => {
    if (!enabled || !borrowVaultAddress || !hasValidSubAccount) return new BigNumber(0)

    return cardBorrowPositions
      .filter((position) => position.liabilityVaultAddress.toLowerCase() === borrowVaultAddress.toLowerCase())
      .reduce((sum, position) => {
        const borrowUSD = new BigNumber(position.borrowAmount).multipliedBy(position.liabilityToken.unitPriceUsd)
        return sum.plus(borrowUSD)
      }, new BigNumber(0))
  }, [enabled, cardBorrowPositions, borrowVaultAddress, hasValidSubAccount])

  // Calculate borrow APY from interest rate
  const borrowAPY = useMemo(() => {
    if (!enabled || !eulerInterestRate) return 0

    const ratePerSecond = Number(formatUnits(eulerInterestRate as bigint, 27))
    const calculatedBorrowAPY = ((1 + ratePerSecond) ** SECONDS_PER_YEAR - 1) * 100

    return calculatedBorrowAPY
  }, [enabled, eulerInterestRate])

  // Filter positions with collateral
  const collateralPositions = useMemo(() => {
    if (!enabled || !hasValidSubAccount) return []
    return eulerPositions.filter((position) => new BigNumber(position.depositAmount).gt(0))
  }, [enabled, eulerPositions, hasValidSubAccount])

  // Fallback path for when the Account Lens query fails (most commonly because
  // the card's liability vault isn't enabled as a controller on the sub-account
  // yet — e.g. a freshly-funded Position 0 with collateral deposits but no
  // borrow yet). In that state `getAccountInfo` returns `queryFailure = true`
  // and `accountLiquidityData` is undefined, which would otherwise blank out
  // the dashboard's spending power even though real borrow capacity exists.
  //
  // Mirrors the `EulerPositionCard` fallback in CardConfiguration: only count
  // deposits that are (a) enabled via the EVC's `getCollaterals` AND (b) live
  // in the same market as the controller's borrow vault, then weight each by
  // that borrow vault's `LTVFull.borrowLTV`. Non-enabled "savings" deposits and
  // deposits in unrelated markets are excluded — preserving the rule that only
  // enabled collateral contributes to spending power.
  const { resolveMarket } = useVaultClassifier()
  const controllerMarketKey = useMemo(() => {
    if (!effectiveLiabilityVault) return undefined
    return resolveMarket(effectiveLiabilityVault)?.key
  }, [effectiveLiabilityVault, resolveMarket])

  const eligibleCollateralItems = useMemo(() => {
    if (!enabled || !hasValidSubAccount) return []
    const enabledSet = new Set((enabledCollateralVaults ?? []).map((v) => v.toLowerCase()))
    const items: { vaultAddress: string; depositAmountUSD: number }[] = []
    for (const pos of collateralPositions) {
      if (!enabledSet.has(pos.vaultAddress.toLowerCase())) continue
      if (controllerMarketKey) {
        const vaultMarketKey = resolveMarket(pos.vaultAddress as Address)?.key
        if (vaultMarketKey !== controllerMarketKey) continue
      }
      items.push({
        vaultAddress: pos.vaultAddress,
        depositAmountUSD: Number(pos.depositAmountUSD),
      })
    }
    return items
  }, [enabled, hasValidSubAccount, collateralPositions, enabledCollateralVaults, controllerMarketKey, resolveMarket])

  const ltvWeightedFallbackUSD = useLtvWeightedCollateral({
    borrowVaultAddress: effectiveLiabilityVault,
    collateral: eligibleCollateralItems,
    chainId,
  })

  // Borrow capacity for this card's sub-account. When the lens returns data we
  // use its LTV-weighted value (authoritative, accounts for per-collateral
  // LTVs). When it doesn't (controller not enabled yet), fall back to the
  // borrow-LTV-weighted sum of enabled+same-market collateral. In both cases
  // non-enabled "savings" deposits do NOT contribute — preserving the rule
  // that only enabled collateral counts toward spending power.
  const maxBorrowCapacityUSD = useMemo(() => {
    if (accountLiquidityData) return accountLiquidityData.maxBorrowCapacityUSD
    return ltvWeightedFallbackUSD ?? 0
  }, [accountLiquidityData, ltvWeightedFallbackUSD])

  // For Euler spend mode, show ALL positions across ALL sub-accounts that are in withdraw-eligible vaults.
  // Spending power = deposit USD - outstanding debt USD on the same sub-account.
  const { address: connectedAddress } = useAccount()
  const { eulerWithdrawVaults } = useSolverOptions()
  const { positions: allEulerPositions } = useELendPositions({
    userAddress: connectedAddress,
    chainId: PROTOCOL_CHAIN_IDS.EULER,
    enabled: enabled && !!connectedAddress,
  })
  const { positions: allBorrowPositions } = useEBorrowPositions({
    userAddress: connectedAddress,
    chainId,
    enabled: enabled && !!connectedAddress,
  })

  const subAccountLiquidityEntries = useMemo(() => {
    if (!enabled || !connectedAddress) return []

    const firstLiabilityVaultBySubAccount = new Map<number, Address>()
    for (const position of allBorrowPositions) {
      if (!firstLiabilityVaultBySubAccount.has(position.subAccountId)) {
        firstLiabilityVaultBySubAccount.set(position.subAccountId, position.liabilityVaultAddress)
      }
    }

    return Array.from(firstLiabilityVaultBySubAccount.entries()).map(([trackedSubAccountId, liabilityVault]) => ({
      trackedSubAccountId,
      liabilityVault,
      subAccountAddress: getSubAccountAddress(connectedAddress, trackedSubAccountId),
    }))
  }, [enabled, connectedAddress, allBorrowPositions])

  const { data: subAccountLiquidityResults } = useReadContracts({
    contracts: subAccountLiquidityEntries.map(({ subAccountAddress, liabilityVault }) => ({
      address: ACCOUNT_LENS_ADDRESS,
      abi: accountLensAbi,
      functionName: 'getAccountInfo' as const,
      args: [subAccountAddress, liabilityVault] as const,
      chainId,
    })),
    query: {
      enabled: enabled && subAccountLiquidityEntries.length > 0,
      staleTime: 30_000,
    },
  })

  // Compute debt info per sub-account (total USD + primary debt token symbol)
  const debtInfoBySubAccount = useMemo(() => {
    const map = new Map<number, { totalUSD: BigNumber; symbol: string }>()
    for (const pos of allBorrowPositions) {
      const current = map.get(pos.subAccountId)
      const debtUSD = new BigNumber(pos.borrowAmount.toString()).multipliedBy(pos.liabilityToken.unitPriceUsd)
      if (current) {
        current.totalUSD = current.totalUSD.plus(debtUSD)
      } else {
        map.set(pos.subAccountId, { totalUSD: debtUSD, symbol: pos.liabilityToken.symbol })
      }
    }
    return map
  }, [allBorrowPositions])

  const spendPowerLiquidityBySubAccount = useMemo(() => {
    const map = new Map<number, EulerSpendPowerLiquidity>()
    if (!subAccountLiquidityResults) return map

    subAccountLiquidityResults.forEach((result, index) => {
      if (result?.status !== 'success') return

      const trackedSubAccountId = subAccountLiquidityEntries[index]?.trackedSubAccountId
      const liquidityInfo = (result.result as { vaultAccountInfo?: { liquidityInfo?: any } })?.vaultAccountInfo
        ?.liquidityInfo

      if (trackedSubAccountId == null || !liquidityInfo || liquidityInfo.queryFailure) return

      // Derive the proper HF the same way the dashboard does:
      // collateralValueLiquidation / liabilityValueLiquidation. Undefined when
      // there is no debt on this sub-account (infinite health).
      const liabilityLiqRaw = (liquidityInfo.liabilityValueLiquidation ?? 0n) as bigint
      const collateralLiqRaw = (liquidityInfo.collateralValueLiquidation ?? 0n) as bigint
      let healthFactor: number | undefined
      if (liabilityLiqRaw > 0n) {
        const liabilityLiq = Number(formatUnits(liabilityLiqRaw, 18))
        const collateralLiq = Number(formatUnits(collateralLiqRaw, 18))
        healthFactor = liabilityLiq > 0 ? collateralLiq / liabilityLiq : undefined
      }

      map.set(trackedSubAccountId, {
        collaterals: liquidityInfo.collaterals ?? [],
        collateralValuesRawUSD: (liquidityInfo.collateralValuesRaw ?? []).map(
          (value: bigint) => new BigNumber(formatUnits(value, 18)),
        ),
        collateralValuesBorrowUSD: (liquidityInfo.collateralValuesBorrowing ?? []).map(
          (value: bigint) => new BigNumber(formatUnits(value, 18)),
        ),
        totalBorrowCapacityUSD: new BigNumber(formatUnits(liquidityInfo.collateralValueBorrowing ?? 0n, 18)),
        totalDebtBorrowUSD: new BigNumber(formatUnits(liquidityInfo.liabilityValueBorrowing ?? 0n, 18)),
        healthFactor,
      })
    })

    return map
  }, [subAccountLiquidityEntries, subAccountLiquidityResults])

  const spendModeCollateralPositions = useMemo(() => {
    if (!enabled || eulerWithdrawVaults.length === 0) return []
    const withdrawVaultAddresses = new Set(eulerWithdrawVaults.map((v) => v.address.toLowerCase()))
    return allEulerPositions
      .filter(
        (position) =>
          new BigNumber(position.depositAmount).gt(0) &&
          withdrawVaultAddresses.has(position.vaultAddress.toLowerCase()),
      )
      .map((position) => {
        const debtInfo = debtInfoBySubAccount.get(position.subAccountId)
        const spendPowerLiquidity = spendPowerLiquidityBySubAccount.get(position.subAccountId)
        const subAccountDebt = debtInfo?.totalUSD ?? new BigNumber(0)
        const depositUSD = new BigNumber(position.depositAmountUSD.toString())
        const spendingPowerUSD = spendPowerLiquidity
          ? calculateEulerSpendPowerUSD({
              collateralVaultAddress: position.vaultAddress,
              depositAmountUSD: depositUSD,
              liquidity: spendPowerLiquidity,
            })
          : BigNumber.max(depositUSD.minus(subAccountDebt), 0)
        return {
          ...position,
          spendingPowerUSD,
          debtUSD: subAccountDebt,
          debtSymbol: debtInfo?.symbol,
          healthFactor: spendPowerLiquidity?.healthFactor,
        }
      })
  }, [enabled, allEulerPositions, eulerWithdrawVaults, debtInfoBySubAccount, spendPowerLiquidityBySubAccount])

  const primaryBorrowPosition = useMemo(() => {
    if (!enabled || !borrowVaultAddress || !hasValidSubAccount) return undefined
    return cardBorrowPositions.find(
      (position) => position.liabilityVaultAddress.toLowerCase() === borrowVaultAddress.toLowerCase(),
    )
  }, [enabled, cardBorrowPositions, borrowVaultAddress, hasValidSubAccount])

  return {
    eulerPositions,
    collateralPositions,
    spendModeCollateralPositions,
    eulerBorrowPositions: cardBorrowPositions, // Only return card sub-account positions
    primaryBorrowPosition,
    totalBorrowUSD,
    borrowToken,
    borrowTokenSymbol: borrowToken?.symbol || selectedBorrowTokenSymbol || 'USDC',
    borrowAPY,
    healthFactor,
    eulerVault,
    totalCollateralUSD,
    maxBorrowCapacityUSD,
    totalDebtValueUSD,
    totalDebtBorrowUSD,
    currentLTV,
    effectiveLiquidationLTV,
    enabledCollateralVaults,
    /**
     * True while we're still fetching position-level data (account liquidity +
     * enabled-collateral set) for the configured sub-account. The caller should
     * show a skeleton rather than the collateral/savings split while this is on.
     */
    isLoadingPositionData: isLoadingAccountLiquidity || isLoadingEnabledCollaterals,
  }
}
